import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Store, 
  Search, 
  MapPin, 
  Phone, 
  Users, 
  Filter,
  Download,
  Save,
  X,
  Package,
  Building,
  Mail
} from 'lucide-react';
import { storesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  address: '',
  city: '',
  phone: '',
  email: ''
};

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await storesAPI.getStores();
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store =>
    String(store.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(store.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(store.address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await storesAPI.updateStore(editingId, form);
        toast.success('Magasin modifié');
      } else {
        await storesAPI.createStore(form);
        toast.success('Magasin ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchStores();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (store) => {
    setForm({
      name: store.name,
      address: store.address || '',
      city: store.city || '',
      phone: store.phone || '',
      email: store.email || ''
    });
    setEditingId(store.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce magasin ?')) return;
    try {
      await storesAPI.deleteStore(id);
      toast.success('Magasin supprimé');
      fetchStores();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const tableColumns = [
    {
      key: 'name',
      label: 'Magasin',
      render: (value, store) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Store className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{store.name}</div>
            <div className="text-sm text-neutral-500">{store.city}</div>
          </div>
        </div>
      )
    },
    {
      key: 'address',
      label: 'Adresse',
      render: (value) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'employee_count',
      label: 'Employés',
      render: (value) => (
        <div className="flex items-center">
          <Users className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || 0}</span>
        </div>
      )
    },
    {
      key: 'product_count',
      label: 'Produits',
      render: (value) => (
        <div className="flex items-center">
          <Package className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || 0}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, store) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(store)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(store)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(store.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalEmployees = stores.reduce((sum, store) => sum + (store.employee_count || 0), 0);
  const totalProducts = stores.reduce((sum, store) => sum + (store.product_count || 0), 0);
  const uniqueCities = new Set(stores.map(s => s.city).filter(Boolean)).size;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Magasins</h1>
          <p className="text-neutral-600">Gérez vos points de vente</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des magasins..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          {hasAnyRole(['admin']) && (
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
            >
              Ajouter un magasin
            </ModernButton>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Magasins</p>
              <p className="text-2xl font-bold text-neutral-900">{stores.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Store className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Employés</p>
              <p className="text-2xl font-bold text-success-600">{totalEmployees}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Produits</p>
              <p className="text-2xl font-bold text-warning-600">{totalProducts}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Villes</p>
              <p className="text-2xl font-bold text-info-600">{uniqueCities}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Stores Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des magasins</h3>
            <p className="text-sm text-neutral-600">{filteredStores.length} magasins trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredStores}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le magasin' : 'Ajouter un magasin'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom du magasin"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Magasin Centre-Ville"
              icon={<Store className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Ville"
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="Ex: Paris"
              icon={<Building className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Adresse"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Ex: 123 Rue de la Paix"
              icon={<MapPin className="w-5 h-5" />}
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Ex: +33 1 23 45 67 89"
              icon={<Phone className="w-5 h-5" />}
            />

            <ModernInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Ex: contact@magasin.com"
              icon={<Mail className="w-5 h-5" />}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Stores; 